// SQLiteService.js – Updated for modern Expo SQLite (promise-based API)
import * as SQLite from 'expo-sqlite';

export class SQLiteService {
  constructor() {
    this.db = null;
    this.isInitialized = false;
    this.dbName = 'jobeng.db';
    this.dbVersion = 1;

    // Table schemas (same as before)
    this.schemas = {
      users: `
        CREATE TABLE IF NOT EXISTS users (
          id TEXT PRIMARY KEY,
          email TEXT UNIQUE NOT NULL,
          name TEXT,
          user_type TEXT,
          company_id TEXT,
          profile_data TEXT,
          created_at TEXT DEFAULT (datetime('now')),
          updated_at TEXT DEFAULT (datetime('now')),
          sync_status TEXT DEFAULT 'pending'
        )
      `,
      jobs: `
        CREATE TABLE IF NOT EXISTS jobs (
          id TEXT PRIMARY KEY,
          title TEXT NOT NULL,
          description TEXT,
          requirements TEXT,
          department TEXT,
          location TEXT,
          employment_type TEXT,
          status TEXT DEFAULT 'draft',
          salary_min INTEGER,
          salary_max INTEGER,
          application_deadline TEXT,
          is_remote INTEGER DEFAULT 0,
          is_urgent INTEGER DEFAULT 0,
          applicants INTEGER DEFAULT 0,
          views INTEGER DEFAULT 0,
          created_at TEXT DEFAULT (datetime('now')),
          updated_at TEXT DEFAULT (datetime('now')),
          sync_status TEXT DEFAULT 'pending',
          user_id TEXT
        )
      `,
      applications: `
        CREATE TABLE IF NOT EXISTS applications (
          id TEXT PRIMARY KEY,
          job_id TEXT NOT NULL,
          applicant_id TEXT NOT NULL,
          status TEXT DEFAULT 'pending',
          cover_letter TEXT,
          resume_url TEXT,
          application_data TEXT,
          applied_at TEXT DEFAULT (datetime('now')),
          updated_at TEXT DEFAULT (datetime('now')),
          sync_status TEXT DEFAULT 'pending'
        )
      `,
      projects: `
        CREATE TABLE IF NOT EXISTS projects (
          id TEXT PRIMARY KEY,
          name TEXT NOT NULL,
          description TEXT,
          status TEXT DEFAULT 'active',
          start_date TEXT,
          end_date TEXT,
          budget REAL,
          client_id TEXT,
          project_data TEXT,
          created_at TEXT DEFAULT (datetime('now')),
          updated_at TEXT DEFAULT (datetime('now')),
          sync_status TEXT DEFAULT 'pending',
          user_id TEXT
        )
      `,
      partners: `
        CREATE TABLE IF NOT EXISTS partners (
          id TEXT PRIMARY KEY,
          name TEXT NOT NULL,
          company TEXT,
          email TEXT,
          phone TEXT,
          industry TEXT,
          partnership_type TEXT,
          status TEXT DEFAULT 'active',
          partner_data TEXT,
          created_at TEXT DEFAULT (datetime('now')),
          updated_at TEXT DEFAULT (datetime('now')),
          sync_status TEXT DEFAULT 'pending',
          user_id TEXT
        )
      `,
      interviews: `
        CREATE TABLE IF NOT EXISTS interviews (
          id TEXT PRIMARY KEY,
          job_id TEXT,
          applicant_id TEXT,
          interviewer_id TEXT,
          scheduled_at TEXT,
          duration INTEGER DEFAULT 60,
          location TEXT,
          interview_type TEXT,
          status TEXT DEFAULT 'scheduled',
          notes TEXT,
          rating INTEGER,
          interview_data TEXT,
          created_at TEXT DEFAULT (datetime('now')),
          updated_at TEXT DEFAULT (datetime('now')),
          sync_status TEXT DEFAULT 'pending'
        )
      `,
      messages: `
        CREATE TABLE IF NOT EXISTS messages (
          id TEXT PRIMARY KEY,
          sender_id TEXT NOT NULL,
          recipient_id TEXT NOT NULL,
          subject TEXT,
          content TEXT NOT NULL,
          message_type TEXT DEFAULT 'text',
          is_read INTEGER DEFAULT 0,
          thread_id TEXT,
          sent_at TEXT DEFAULT (datetime('now')),
          sync_status TEXT DEFAULT 'pending'
        )
      `,
      sync_queue: `
        CREATE TABLE IF NOT EXISTS sync_queue (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          table_name TEXT NOT NULL,
          record_id TEXT NOT NULL,
          operation TEXT NOT NULL,
          data TEXT,
          created_at TEXT DEFAULT (datetime('now')),
          retry_count INTEGER DEFAULT 0,
          last_error TEXT
        )
      `,
      app_settings: `
        CREATE TABLE IF NOT EXISTS app_settings (
          key TEXT PRIMARY KEY,
          value TEXT,
          updated_at TEXT DEFAULT (datetime('now'))
        )
      `
    };
  }

  // Initialize the SQLite database (async)
  async initialize() {
    try {
      // Open the database asynchronously
      this.db = await SQLite.openDatabaseAsync(this.dbName);

      // Create all tables using exec (runs multiple statements)
      const createStatements = Object.values(this.schemas).join('; ');
      await this.db.execAsync(createStatements);

      // Run migrations
      await this.runMigrations();

      this.isInitialized = true;
      console.log('✅ SQLiteService initialized successfully (modern API)');
      return { success: true };
    } catch (error) {
      console.error('❌ SQLiteService initialization error:', error);
      return { success: false, error: error.message };
    }
  }

  // Run database migrations
  async runMigrations() {
    try {
      const currentVersion = await this.getDatabaseVersion();
      if (currentVersion < this.dbVersion) {
        // Add any migration steps here if needed
        await this.setDatabaseVersion(this.dbVersion);
        console.log(`📊 Database migrated to version ${this.dbVersion}`);
      }
    } catch (error) {
      console.error('Migration error:', error);
      throw error;
    }
  }

  // Get database version from app_settings
  async getDatabaseVersion() {
    try {
      const row = await this.db.getFirstAsync(
        'SELECT value FROM app_settings WHERE key = ?',
        ['db_version']
      );
      return row ? parseInt(row.value) : 0;
    } catch {
      return 0; // Table might not exist yet
    }
  }

  // Set database version
  async setDatabaseVersion(version) {
    await this.db.runAsync(
      'INSERT OR REPLACE INTO app_settings (key, value) VALUES (?, ?)',
      ['db_version', version.toString()]
    );
  }

  // ==================== GENERIC CRUD ====================

  async insert(tableName, data) {
    try {
      const id = data.id || this.generateId();
      const now = new Date().toISOString();
      const insertData = {
        ...data,
        id,
        created_at: now,
        updated_at: now,
        sync_status: 'pending'
      };

      const columns = Object.keys(insertData).join(', ');
      const placeholders = Object.keys(insertData).map(() => '?').join(', ');
      const values = Object.values(insertData);

      const query = `INSERT INTO ${tableName} (${columns}) VALUES (${placeholders})`;
      await this.db.runAsync(query, values);

      // Add to sync queue
      await this.addToSyncQueue(tableName, id, 'INSERT', data);

      return { success: true, id, data: insertData };
    } catch (error) {
      console.error(`Insert failed for ${tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  async update(tableName, id, data) {
    try {
      const updateData = {
        ...data,
        updated_at: new Date().toISOString(),
        sync_status: 'pending'
      };

      const setClause = Object.keys(updateData).map(key => `${key} = ?`).join(', ');
      const values = [...Object.values(updateData), id];

      const query = `UPDATE ${tableName} SET ${setClause} WHERE id = ?`;
      const result = await this.db.runAsync(query, values);

      if (result.changes > 0) {
        await this.addToSyncQueue(tableName, id, 'UPDATE', data);
        // Fetch updated record
        const updated = await this.getById(tableName, id);
        return { success: true, data: updated.data };
      } else {
        return { success: false, error: 'Record not found' };
      }
    } catch (error) {
      console.error(`Update failed for ${tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  async delete(tableName, id) {
    try {
      const query = `DELETE FROM ${tableName} WHERE id = ?`;
      const result = await this.db.runAsync(query, [id]);

      if (result.changes > 0) {
        await this.addToSyncQueue(tableName, id, 'DELETE');
        return { success: true };
      } else {
        return { success: false, error: 'Record not found' };
      }
    } catch (error) {
      console.error(`Delete failed for ${tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  async getById(tableName, id) {
    try {
      const row = await this.db.getFirstAsync(
        `SELECT * FROM ${tableName} WHERE id = ?`,
        [id]
      );
      if (row) {
        return { success: true, data: row };
      } else {
        return { success: false, error: 'Record not found' };
      }
    } catch (error) {
      console.error(`getById failed for ${tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  async findAll(tableName, conditions = {}, options = {}) {
    try {
      let query = `SELECT * FROM ${tableName}`;
      const values = [];

      // WHERE clause
      if (Object.keys(conditions).length > 0) {
        const whereClause = Object.keys(conditions).map(key => `${key} = ?`).join(' AND ');
        query += ` WHERE ${whereClause}`;
        values.push(...Object.values(conditions));
      }

      // ORDER BY
      if (options.orderBy) {
        query += ` ORDER BY ${options.orderBy}`;
        if (options.orderDirection) {
          query += ` ${options.orderDirection}`;
        }
      }

      // LIMIT / OFFSET
      if (options.limit) {
        query += ` LIMIT ${options.limit}`;
        if (options.offset) {
          query += ` OFFSET ${options.offset}`;
        }
      }

      const rows = await this.db.getAllAsync(query, values);
      return { success: true, data: rows };
    } catch (error) {
      console.error(`findAll failed for ${tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  // ==================== SYNC QUEUE ====================

  async addToSyncQueue(tableName, recordId, operation, data = null) {
    try {
      await this.db.runAsync(
        `INSERT INTO sync_queue (table_name, record_id, operation, data, created_at, retry_count)
         VALUES (?, ?, ?, ?, datetime('now'), 0)`,
        [tableName, recordId.toString(), operation, data ? JSON.stringify(data) : null]
      );
    } catch (error) {
      console.error('Error adding to sync queue:', error);
    }
  }

  async getSyncQueue(limit = 100) {
    try {
      const rows = await this.db.getAllAsync(
        `SELECT * FROM sync_queue ORDER BY created_at ASC LIMIT ?`,
        [limit]
      );
      return { success: true, data: rows };
    } catch (error) {
      console.error('Error getting sync queue:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== JOB OPERATIONS ====================

  async getJobs(userId, filters = {}) {
    const conditions = { user_id: userId };
    if (filters.status) conditions.status = filters.status;
    if (filters.department) conditions.department = filters.department;
    if (filters.location) conditions.location = filters.location;

    return this.findAll('jobs', conditions, { orderBy: 'created_at', orderDirection: 'DESC' });
  }

  async createJob(jobData) {
    const job = {
      id: jobData.id || `job_${Date.now()}`,
      ...jobData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      sync_status: 'pending'
    };
    return this.insert('jobs', job);
  }

  async updateJob(jobId, updateData) {
    return this.update('jobs', jobId, updateData);
  }

  async deleteJob(jobId) {
    return this.delete('jobs', jobId);
  }

  // ==================== PROJECT OPERATIONS ====================

  async getProjects(userId, filters = {}) {
    const conditions = { user_id: userId };
    if (filters.status) conditions.status = filters.status;
    return this.findAll('projects', conditions, { orderBy: 'created_at', orderDirection: 'DESC' });
  }

  async createProject(projectData) {
    const project = {
      id: projectData.id || `proj_${Date.now()}`,
      ...projectData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      sync_status: 'pending'
    };
    return this.insert('projects', project);
  }

  // ==================== ANALYTICS ====================

  async getJobAnalytics(userId) {
    try {
      const totalJobs = await this.db.getFirstAsync(
        'SELECT COUNT(*) as count FROM jobs WHERE user_id = ?', [userId]
      );
      const activeJobs = await this.db.getFirstAsync(
        'SELECT COUNT(*) as count FROM jobs WHERE user_id = ? AND status = "active"', [userId]
      );
      const totalApplicants = await this.db.getFirstAsync(
        'SELECT SUM(applicants) as total FROM jobs WHERE user_id = ?', [userId]
      );
      const totalViews = await this.db.getFirstAsync(
        'SELECT SUM(views) as total FROM jobs WHERE user_id = ?', [userId]
      );
      const pendingSync = await this.db.getFirstAsync(
        'SELECT COUNT(*) as count FROM sync_queue'
      );

      const results = {
        totalJobs: totalJobs?.count || 0,
        activeJobs: activeJobs?.count || 0,
        totalApplicants: totalApplicants?.total || 0,
        totalViews: totalViews?.total || 0,
        pendingSync: pendingSync?.count || 0
      };
      results.conversionRate = results.totalViews > 0
        ? Math.round((results.totalApplicants / results.totalViews) * 100)
        : 0;

      return { success: true, data: results };
    } catch (error) {
      console.error('Error getting job analytics:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== UTILITY ====================

  generateId() {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  async clearAllData() {
    try {
      const tables = Object.keys(this.schemas).filter(t => t !== 'app_settings');
      for (const table of tables) {
        await this.db.runAsync(`DELETE FROM ${table}`);
      }
      return { success: true };
    } catch (error) {
      console.error('Error clearing all data:', error);
      return { success: false, error: error.message };
    }
  }

  async exportData(tableName) {
    return this.findAll(tableName);
  }

  async close() {
    try {
      // No explicit close needed for modern API, but we can null the reference
      this.db = null;
      this.isInitialized = false;
      return { success: true };
    } catch (error) {
      console.error('Error closing database:', error);
      return { success: false, error: error.message };
    }
  }
}